import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { questionsRead } from '../components/Questions/useUnread';
import Conversation, { SafetyNote } from '../components/Questions/Conversation';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });
const ago = (v) => {
  const d = v ? new Date(v) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  const m = Math.round((Date.now() - d.getTime()) / 60000);
  return m < 60 ? `${Math.max(1, m)} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
};

/* /teacher/questions: my students' questions (RoboMind and the parents can read them) */
const StudentQuestions = () => {
  const [params, setParams] = useSearchParams();
  const current = params.get('student');
  const [list, setList] = useState(null);
  const [conv, setConv] = useState(null);
  const [error, setError] = useState('');
  const [activeOnly, setActiveOnly] = useState(() => {
    try {
      return localStorage.getItem('rm-questions-active') !== '0';
    } catch {
      return true;
    }
  });
  const toggleActive = (on) => {
    setActiveOnly(on);
    try {
      localStorage.setItem('rm-questions-active', on ? '1' : '0');
    } catch {
      /* private mode */
    }
  };

  const loadList = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/teacher/questions`, auth());
      setList(res.data.students || []);
    } catch (err) {
      setError(err.response?.data?.error || 'We couldn’t load the questions.');
    }
  }, []);
  const loadConv = useCallback(async () => {
    if (!current) return setConv(null);
    try {
      const res = await axios.get(`${API}/api/teacher/questions/${current}`, auth());
      setConv(res.data);
      questionsRead(); // the menu badge updates
    } catch (err) {
      setConv({ error: err.response?.data?.error || 'We couldn’t load the conversation.' });
    }
    return undefined;
  }, [current]);

  useEffect(() => {
    loadList();
  }, [loadList]);
  useEffect(() => {
    loadConv().then(loadList);
    const id = setInterval(() => {
      loadConv();
      loadList();
    }, 60000);
    return () => clearInterval(id);
  }, [loadConv, loadList]);

  const open = (id) => setParams({ student: id }, { replace: true });
  // active students (class in the last 30 days or coming up); a student with unread messages always shows
  const shown = (list || []).filter((s) => !activeOnly || s.active !== false || s.unread > 0 || s.id === current);
  const hiddenCount = (list || []).length - shown.length;
  const send = async (body, link) => {
    await axios.post(`${API}/api/teacher/questions/${current}`, { body, link }, auth());
    await loadConv();
    await loadList();
  };
  const report = async (m) => {
    const note = window.prompt('Why are you reporting this message? (optional)');
    if (note === null) return;
    await axios.post(`${API}/api/teacher/questions/report/${m.id}`, { note }, auth());
    await loadConv();
  };

  return (
    <div className="rmq">
      <header className="rmq-head">
        <span className="rmq-eyebrow">My students</span>
        <h1>Student questions</h1>
        <p>Your students’ questions about classes and projects. Try to answer within 24 hours.</p>
      </header>
      <SafetyNote me="teacher" />
      {error && (
        <p className="rmq-error">
          <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
        </p>
      )}
      {!list ? (
        <div className="rmq-skel" aria-busy="true" />
      ) : (
        <div className="rmq-split">
          <ul className="rmq-list" aria-label="Students">
            <li className="rmq-filter">
              <label>
                <input type="checkbox" checked={activeOnly} onChange={(e) => toggleActive(e.target.checked)} />
                <span>
                  Current active students only{hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ''}
                </span>
              </label>
            </li>
            {list.length === 0 && <li className="rmq-pick">No students assigned to you yet.</li>}
            {list.length > 0 && shown.length === 0 && <li className="rmq-pick">No active students. Untick the box to see all.</li>}
            {shown.map((s) => (
              <li key={s.id}>
                <button type="button" className={current === s.id ? 'is-on' : ''} onClick={() => open(s.id)}>
                  <img src={s.photo} alt="" onError={(e) => (e.currentTarget.style.visibility = 'hidden')} />
                  <span>
                    <b>{s.name}</b>
                    <small>{s.last ? `${s.last} · ${ago(s.lastAt)}` : 'No messages yet'}</small>
                  </span>
                  {s.unread > 0 && <span className="rmq-badge">{s.unread}</span>}
                </button>
              </li>
            ))}
          </ul>
          <section className="rmq-card">
            {!current ? (
              <div className="rmq-pick">Choose a student to read and answer their questions.</div>
            ) : !conv ? (
              <div className="rmq-skel" aria-busy="true" />
            ) : conv.error ? (
              <div className="rmq-pick">{conv.error}</div>
            ) : (
              <>
                <div className="rmq-card-head">
                  <img src={conv.student.photo} alt="" onError={(e) => (e.currentTarget.style.visibility = 'hidden')} />
                  <div>
                    <b>{conv.student.name}</b>
                    <small>Your student</small>
                  </div>
                </div>
                <Conversation
                  me="teacher"
                  names={{ student: conv.student.name.split(' ')[0], teacher: 'You' }}
                  messages={conv.messages}
                  onSend={send}
                  onReport={report}
                  placeholder={`Answer ${conv.student.name.split(' ')[0]}…`}
                  emptyText="No messages yet. You can write first, e.g. a tip for their project."
                />
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default StudentQuestions;
